import { useEffect, useState, useMemo, useCallback } from 'react'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import { useTranslation } from '../../i18n'
import { trainingApi } from '../../api'
import type { TrainingSetWithUser } from '../../types/api'
import { useTheme } from '../../hooks/useTheme'
import { useAuth } from '../../contexts/AuthContext'
import { formatDateRange } from '../../utils/dateTranslations'
import { Loading, Avatar, Card, ErrorMessage, Badge } from '../ui'
import { TRAINING_SET_STATUS } from '../../constants'
import { isCoachedTrainee } from '../../utils/entitlements'

interface LocationState {
  initialFilter?: 'all' | 'canBeCompleted' | 'unpaid'
}

type TrainingSetFilter = 'all' | 'canBeCompleted' | 'unpaid'

export default function AllTrainingSets() {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const { t } = useTranslation()
  const { user } = useAuth()

  const state = location.state as LocationState | null
  const [trainingSetsWithUsers, setTrainingSetsWithUsers] = useState<TrainingSetWithUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<TrainingSetFilter>(state?.initialFilter || 'all')

  const loadData = useCallback(async () => {
    if (!user?.id || isCoachedTrainee(user)) return
    setLoading(true)
    setError(null)
    try {
      const sets = await trainingApi.getAllTrainingSets()
      setTrainingSetsWithUsers(sets)
    } catch (err) {
      console.error('Error loading training sets:', err)
      setError(err instanceof Error ? err.message : 'Failed to load training sets')
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    loadData()
  }, [loadData])

  const filteredSets = useMemo(() => {
    let filtered = trainingSetsWithUsers
    if (filter === 'all') {
      filtered = trainingSetsWithUsers.filter(s => s.trainingSet.status === TRAINING_SET_STATUS.ACTIVE)
    } else if (filter === 'canBeCompleted') {
      filtered = trainingSetsWithUsers.filter(
        s => s.canBeCompleted
          && s.isCurrentUserCoach
          && s.trainingSet.status === TRAINING_SET_STATUS.ACTIVE
      )
    } else if (filter === 'unpaid') {
      filtered = trainingSetsWithUsers.filter(s => s.trainingSet.unpaid === true)
    }
    return [...filtered].sort((a, b) =>
      new Date(b.trainingSet.startDate).getTime() - new Date(a.trainingSet.startDate).getTime()
    )
  }, [trainingSetsWithUsers, filter])

  const unpaidCount = useMemo(
    () => trainingSetsWithUsers.filter(s => s.trainingSet.unpaid === true).length,
    [trainingSetsWithUsers]
  )

  const canBeCompletedCount = useMemo(
    () => trainingSetsWithUsers.filter(
      s => s.canBeCompleted
        && s.isCurrentUserCoach
        && s.trainingSet.status === TRAINING_SET_STATUS.ACTIVE
    ).length,
    [trainingSetsWithUsers]
  )

  const handleSetClick = (setWithUser: TrainingSetWithUser) => {
    const { trainingSet, isCurrentUserCoach } = setWithUser
    const userId = isCurrentUserCoach ? trainingSet.traineeId : trainingSet.coachId
    const userType = isCurrentUserCoach ? 'trainee' : 'coach'
    navigate(`/${userType}/${userId}/set/${trainingSet.id}`)
  }

  if (isCoachedTrainee(user)) {
    return <Navigate to="/active-training-set" replace />
  }

  if (loading) return <Loading fullScreen />

  if (error) {
    return (
      <ErrorMessage
        title={t.allTrainingSets.error.title}
        message={error}
        icon="⚠️"
        onRetry={loadData}
        retryLabel={t.allTrainingSets.error.tryAgain}
        fullScreen
      />
    )
  }

  const filters: { label: string; value: TrainingSetFilter }[] = [
    { label: t.allTrainingSets.filters.all, value: 'all' },
    {
      label: t.allTrainingSets.filters.canBeCompleted
        + (canBeCompletedCount > 0 ? ` ${canBeCompletedCount}` : ''),
      value: 'canBeCompleted',
    },
    {
      label: t.allTrainingSets.filters.unpaid
        + (unpaidCount > 0 ? ` ${unpaidCount}` : ''),
      value: 'unpaid',
    },
  ]

  return (
    <div className="p-5">
      <header className="mb-6" data-tour="tour-page-sets">
        <h1 className="text-2xl font-bold mb-1" style={{ color: theme.text.primary }}>
          {t.allTrainingSets.title}
        </h1>
        <p className="text-sm" style={{ color: theme.text.muted }}>
          {t.allTrainingSets.subtitle}
        </p>
      </header>

      <div className="flex gap-2 overflow-x-auto pb-1 mb-4" style={{ scrollbarWidth: 'none' }}>
        {filters.map(f => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className="px-3.5 py-1.5 rounded-full text-xs font-semibold flex-shrink-0 transition-colors"
            style={filter === f.value
              ? { backgroundColor: theme.buttonColor, color: theme.buttonTextColor }
              : { backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}`, color: theme.text.muted }
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      {filteredSets.length > 0 ? (
        <div className="flex flex-col gap-3">
          {filteredSets.map((setWithUser) => {
            const { trainingSet, relatedUser, canBeCompleted, isCurrentUserCoach } = setWithUser
            const displayName = relatedUser?.firstName
              ? `${relatedUser.firstName} ${relatedUser.lastName || ''}`.trim()
              : relatedUser?.username || `User ${relatedUser?.id}`

            return (
              <Card
                key={trainingSet.id}
                variant="brand"
                onClick={() => handleSetClick(setWithUser)}
                showArrow
              >
                <div className="flex items-start gap-3 pr-6">
                  <Avatar
                    photoUrl={relatedUser?.photoUrl}
                    name={displayName}
                    size="sm"
                    fallbackBg={theme.accent.light}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: theme.text.primary }}>
                      {displayName}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: theme.text.muted }}>
                      {formatDateRange(
                        new Date(trainingSet.startDate),
                        new Date(trainingSet.calculatedEndDate),
                        t
                      )}
                      {' · '}
                      {trainingSet.plannedTrainings} {t.trainingSetStats.planned.toLowerCase()}
                    </p>
                    {(canBeCompleted
                      && isCurrentUserCoach
                      && trainingSet.status === TRAINING_SET_STATUS.ACTIVE)
                      || trainingSet.unpaid === true ? (
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {canBeCompleted
                          && isCurrentUserCoach
                          && trainingSet.status === TRAINING_SET_STATUS.ACTIVE && (
                            <Badge
                              variant="success"
                              size="sm"
                              className="rounded-md flex-shrink-0"
                            >
                              {t.allTrainingSets.canComplete}
                            </Badge>
                          )}
                        {trainingSet.unpaid === true && (
                          <Badge
                            variant="warning"
                            size="sm"
                            className="rounded-md flex-shrink-0"
                          >
                            {t.allTrainingSets.unpaid}
                          </Badge>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center py-14 text-center">
          <span className="text-5xl mb-4">📋</span>
          <p className="font-semibold mb-1" style={{ color: theme.text.primary }}>
            {t.allTrainingSets.emptyState.title}
          </p>
          <p className="text-sm px-8" style={{ color: theme.text.muted }}>
            {t.allTrainingSets.emptyState.description}
          </p>
        </div>
      )}
    </div>
  )
}
